// HTTP-клиент API. Пути пишутся без /api: api.get('/settings') → GET /api/settings
// (путь, уже начинающийся с /api/, остаётся как есть).
// Ошибки — ApiError {status, code, message, field} по правилу 7; нет сети — status 0
// (у записи текст «Нет связи, сохранить не получится»).
// Ответ 401 (кроме /api/auth/*) вызывает обработчик входа: приложение переводит на /login,
// очередь офлайн-мутаций при этом не трогается. Ответ 423 (сессия заблокирована) вызывает
// обработчик блокировки. Запрос открытого на экране приложения несёт заголовок активности —
// только он продлевает незаблокированную сессию (см. src/lock).

import { captureIdentity, IDENTITY_HEADER } from './identity'

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly field: string | null

  constructor(status: number, code: string, message: string, field: string | null = null) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.field = field
  }

  get isNetwork(): boolean {
    return this.status === 0
  }
}

const NETWORK_MESSAGE = 'Нет связи с сервером'
/** Запись без сети: офлайн сохраняется только тренировка (через очередь), остальное — нет. */
const NETWORK_SAVE_MESSAGE = 'Нет связи, сохранить не получится'

export type QueryValue = string | number | boolean | null | undefined

export type RequestOptions = {
  query?: Record<string, QueryValue>
  headers?: Record<string, string>
  signal?: AbortSignal
}

type UnauthorizedHandler = (() => void) | null

function defaultUnauthorized(): void {
  const { pathname, search } = window.location
  if (pathname === '/login' || pathname === '/setup') return
  window.location.assign(`/login?next=${encodeURIComponent(pathname + search)}`)
}

let onUnauthorized: UnauthorizedHandler = null
let onLocked: (() => void) | null = null
let isActive: () => boolean = () => false

export const ACTIVE_HEADER = 'X-Health-Active'

/** Что делать при 401: приложение ставит переход на вход; null — поведение по умолчанию. */
export function setUnauthorizedHandler(handler: UnauthorizedHandler): void {
  onUnauthorized = handler
}

/** Что делать при 423 (сессия заблокирована) и когда слать заголовок активности. */
export function setLockHandlers(handlers: { onLocked: () => void; isActive: () => boolean } | null): void {
  onLocked = handlers?.onLocked ?? null
  isActive = handlers?.isActive ?? (() => false)
}

export function apiUrl(path: string, query?: Record<string, QueryValue>): string {
  const base = path === '/api' || path.startsWith('/api/') ? path : `/api${path.startsWith('/') ? '' : '/'}${path}`
  if (!query) return base
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === null || value === undefined || value === '') continue
    params.append(key, String(value))
  }
  const qs = params.toString()
  return qs ? `${base}${base.includes('?') ? '&' : '?'}${qs}` : base
}

async function parseBody(response: Response): Promise<unknown> {
  if (response.status === 204 || response.status === 205) return undefined
  const text = await response.text()
  if (!text) return undefined
  try {
    return JSON.parse(text) as unknown
  } catch {
    return text
  }
}

function toApiError(status: number, body: unknown): ApiError {
  if (body && typeof body === 'object' && 'error' in body) {
    const error = (body as { error?: { code?: unknown; message?: unknown; field?: unknown } }).error
    if (error && typeof error.message === 'string') {
      return new ApiError(
        status,
        typeof error.code === 'string' ? error.code : 'http_error',
        error.message,
        typeof error.field === 'string' ? error.field : null,
      )
    }
  }
  return new ApiError(status, 'http_error', `Ошибка сервера (${status})`)
}

export async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  options: RequestOptions = {},
): Promise<T> {
  const url = apiUrl(path, options.query)
  const identity = captureIdentity()
  const privateRequest = !url.startsWith('/api/auth/')
  const checkLateIdentity = !/^\/api\/auth\/(login(?:\/|$)|setup(?:\/|$)|invite|reset|confirm)/.test(url)
  if (privateRequest && !identity.id) throw new ApiError(401, 'unauthorized', 'Подтверди вход')
  const headers = new Headers(options.headers)
  if (identity.id && !headers.has(IDENTITY_HEADER)) headers.set(IDENTITY_HEADER, identity.id)
  headers.set('Accept', 'application/json')
  if (isActive()) headers.set(ACTIVE_HEADER, '1')
  let payload: BodyInit | undefined
  if (body instanceof FormData || body instanceof Blob) {
    payload = body
  } else if (body !== undefined) {
    headers.set('Content-Type', 'application/json')
    payload = JSON.stringify(body)
  }

  let response: Response
  try {
    response = await fetch(url, {
      method,
      headers,
      body: payload,
      credentials: 'same-origin',
      signal: options.signal,
    })
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    const message = method.toUpperCase() === 'GET' ? NETWORK_MESSAGE : NETWORK_SAVE_MESSAGE
    throw new ApiError(0, 'network_error', message)
  }

  const data = await parseBody(response)
  if (checkLateIdentity && identity.id && !identity.isCurrent()) {
    throw new ApiError(409, 'identity_changed', 'Аккаунт изменился. Подтверди вход заново')
  }
  if (!response.ok) {
    const error = toApiError(response.status, data)
    if (response.status === 401 && !url.startsWith('/api/auth/')) {
      ;(onUnauthorized ?? defaultUnauthorized)()
    }
    if (response.status === 423) onLocked?.()
    throw error
  }
  return data as T
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => request<T>('GET', path, undefined, options),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('POST', path, body, options),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('PUT', path, body, options),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('PATCH', path, body, options),
  delete: <T = void>(path: string, options?: RequestOptions) =>
    request<T>('DELETE', path, undefined, options),
}

/** Текст ошибки для человека из чего угодно, что прилетело в catch/onError. */
export function errorMessage(error: unknown, fallback = 'Что-то пошло не так'): string {
  return error instanceof ApiError ? error.message : fallback
}
